import { hexToRgb } from "./baseStyleUtils";
import {
  FONT_FAMILY_KEYS,
  getFontFamilyClass as resolveFontFamilyClass,
  getFontFamilyStyle as resolveFontFamilyStyle,
} from "./fontFamilyUtils";
// Note: offset-copy rendering is handled in renderer components (Title/Text) so it can support scaling.

// Default font sizes map based on the FontSizes model with actual pixel values
export const DEFAULT_FONT_SIZES = {
  1: 48,
  2: 42,
  3: 36,
  4: 30,
  5: 24,
  6: 20,
  text: 16,
};

export const DEFAULT_TEXT_COLOR = "#000000";
export const TAILWIND_COLORS = [
  "slate",
  "gray",
  "zinc",
  "neutral",
  "stone",
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
];
export const TAILWIND_COLOR_REGEX = new RegExp(
  `^(${TAILWIND_COLORS.join("|")})-\\d{2,3}$`,
);

export const FONT_FAMILY_CHOICES = FONT_FAMILY_KEYS;

/**
 * Gets Tailwind font family class based on font family name
 * @param {string} fontFamily - Font family name
 * @returns {string} - Tailwind CSS font family class
 */
export function getFontFamilyClass(fontFamily) {
  return resolveFontFamilyClass(fontFamily);
}

export function getFontFamilyStyle(fontFamily) {
  return resolveFontFamilyStyle(fontFamily);
}

// Text positioning maps
const VERTICAL_ALIGNMENT_CLASSES = {
  top: "items-start",
  middle: "items-center",
  bottom: "items-end",
};

const HORIZONTAL_ALIGNMENT_CLASSES = {
  left: "justify-start text-left",
  center: "justify-center text-center",
  right: "justify-end text-right",
};

// Text transform maps
const TEXT_TRANSFORM_CLASSES = {
  grow: "scale-110",
  shrink: "scale-90",
  bold: "font-bold",
  italic: "skew-x-6",  // Transform-based for smooth transition
  underline: "underline decoration-2",
};

const TRANSITION_CLASS = "transition-all duration-300 ease-in-out";

export function getFontSizeClass(fontSizePx) {
  if (fontSizePx && Number.isFinite(Number(fontSizePx))) {
    return `text-[${Number(fontSizePx)}px]`;
  }
  return "";
}

export function resolveFontSizePx(characterStyle, preview) {
  if (!characterStyle) return null;
  const byLayout = characterStyle.font_size;
  if (!byLayout || typeof byLayout !== "object") {
    return null;
  }
  let w = typeof window !== "undefined" ? window.innerWidth : 1920;
  if (preview && typeof preview.contentWidth === "number") {
    w = preview.contentWidth;
  } else if (preview && preview.mode !== "responsive" && preview.viewport?.width) {
    w = preview.viewport.width;
  }
  if (w < 640) return byLayout.extra_small ?? byLayout.small ?? byLayout.medium ?? byLayout.large ?? byLayout.extra_large ?? null;
  if (w < 768) return byLayout.small ?? byLayout.extra_small ?? byLayout.medium ?? byLayout.large ?? byLayout.extra_large ?? null;
  if (w < 1024) return byLayout.medium ?? byLayout.large ?? byLayout.small ?? byLayout.extra_large ?? byLayout.extra_small ?? null;
  if (w < 1280) return byLayout.large ?? byLayout.medium ?? byLayout.extra_large ?? byLayout.small ?? byLayout.extra_small ?? null;
  return byLayout.extra_large ?? byLayout.large ?? byLayout.medium ?? byLayout.small ?? byLayout.extra_small ?? null;
}

// Pick the breakpoint key that matches the current rendering width, mirroring
// ``resolveFontSizePx``'s breakpoint cutoffs so every responsive wrapper
// resolves consistently. Used by both ``resolveTextPosition`` and
// ``resolveButtonCollectionAlignment``.
function _pickBreakpointKey(preview) {
  let w = typeof window !== "undefined" ? window.innerWidth : 1920;
  if (preview && typeof preview.contentWidth === "number") {
    w = preview.contentWidth;
  } else if (preview && preview.mode !== "responsive" && preview.viewport?.width) {
    w = preview.viewport.width;
  }
  if (w < 640) return "extra_small";
  if (w < 768) return "small";
  if (w < 1024) return "medium";
  if (w < 1280) return "large";
  return "extra_large";
}

// Walk the breakpoint chain so an unset value at the current breakpoint falls
// back to the nearest defined neighbour. Mirrors ``resolveFontSizePx``'s
// fallback semantics so partial wrappers stay renderable.
function _firstDefinedFromChain(byLayout, chain) {
  for (const key of chain) {
    const value = byLayout?.[key];
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }
  return null;
}

const _BREAKPOINT_FALLBACK_CHAINS = {
  extra_small: ["extra_small", "small", "medium", "large", "extra_large"],
  small: ["small", "extra_small", "medium", "large", "extra_large"],
  medium: ["medium", "large", "small", "extra_large", "extra_small"],
  large: ["large", "medium", "extra_large", "small", "extra_small"],
  extra_large: ["extra_large", "large", "medium", "small", "extra_small"],
};

/**
 * Resolve the active ``text_position`` string for the current viewport.
 *
 * Backwards-compatible with three input shapes:
 *   - ``string``: the legacy single value (e.g. ``"middle-center"``);
 *     returned as-is so older payloads or hand-rolled test fixtures keep
 *     working.
 *   - ``object``: a serialized ``ResponsiveTextPosition`` wrapper with
 *     per-breakpoint keys (``extra_small``..``extra_large``); the matching
 *     breakpoint is picked using the same cutoffs as ``resolveFontSizePx``,
 *     falling back to neighbouring breakpoints when a value is missing.
 *   - ``null``/``undefined``: returns ``null`` so callers can apply their
 *     own renderer-specific default (e.g. ``"middle-full"`` for Text).
 *
 * @param {string | { extra_small?: string, small?: string, medium?: string,
 *   large?: string, extra_large?: string } | null | undefined} textPosition
 * @param {{ contentWidth?: number, mode?: string, viewport?: { width?: number } } | undefined} preview
 * @returns {string | null}
 */
export function resolveTextPosition(textPosition, preview) {
  if (textPosition === null || textPosition === undefined) return null;
  if (typeof textPosition === "string") return textPosition;
  if (typeof textPosition !== "object") return null;
  const key = _pickBreakpointKey(preview);
  return _firstDefinedFromChain(textPosition, _BREAKPOINT_FALLBACK_CHAINS[key]);
}

/**
 * Resolve the active ``ButtonCollection.alignment`` string for the current
 * viewport. Same string/object/null tolerance as ``resolveTextPosition``.
 *
 * @param {string | { extra_small?: string, small?: string, medium?: string,
 *   large?: string, extra_large?: string } | null | undefined} alignment
 * @param {{ contentWidth?: number, mode?: string, viewport?: { width?: number } } | undefined} preview
 * @returns {string | null}
 */
export function resolveButtonCollectionAlignment(alignment, preview) {
  if (alignment === null || alignment === undefined) return null;
  if (typeof alignment === "string") return alignment;
  if (typeof alignment !== "object") return null;
  const key = _pickBreakpointKey(preview);
  return _firstDefinedFromChain(alignment, _BREAKPOINT_FALLBACK_CHAINS[key]);
}

export function getFontWeightClass(
  fontBold,
  fontCustomWeightEnabled,
  fontCustomWeight,
) {
  if (!fontCustomWeightEnabled || fontCustomWeightEnabled === null) {
    return fontBold ? "font-bold" : "";
  }
  return fontCustomWeight !== null && fontCustomWeight !== undefined
    ? `font-[${fontCustomWeight}]`
    : "";
}

export function getTextColorStyle(textColor, opacity = 100) {
  if (!textColor) textColor = DEFAULT_TEXT_COLOR;
  if (textColor.startsWith("#")) {
    const style = {};
    if (opacity !== 100 && opacity !== undefined) {
      const rgbValues = hexToRgb(textColor);
      style.color = `rgba(${rgbValues}, ${opacity / 100})`;
    } else {
      style.color = textColor;
    }
    return { className: "", style };
  }
  const className = TAILWIND_COLOR_REGEX.test(textColor)
    ? `text-${textColor}`.trim()
    : `text-${textColor}-500`.trim();
  if (opacity !== 100 && opacity !== undefined) {
    className += ` text-opacity-[${opacity / 100}]`;
  }
  return { className, style: {} };
}

export function getBackgroundColorStyle(backgroundColor) {
  if (!backgroundColor) return { className: "", style: {} };

  if (
    backgroundColor.startsWith("#") ||
    backgroundColor.startsWith("rgba") ||
    backgroundColor.startsWith("rgb")
  ) {
    return { className: "", style: { backgroundColor } };
  }

  // Handle standard colors that don't need shades
  if (
    ["white", "black", "transparent", "inherit", "current"].includes(
      backgroundColor,
    )
  ) {
    return { className: `bg-${backgroundColor}`, style: {} };
  }

  const className = TAILWIND_COLOR_REGEX.test(backgroundColor)
    ? `bg-${backgroundColor}`.trim()
    : `bg-${backgroundColor}-500`.trim();

  return { className, style: {} };
}

/**
 * Gets Tailwind italic class if font style is italic
 * @param {string} fontStyle - Font style value ('italic' or 'normal')
 * @returns {string} - Tailwind CSS italic class or empty string
 */
export function getFontItalicClass(fontItalic) {
  return fontItalic ? "italic" : "";
}

/**
 * Gets Tailwind text decoration classes
 * @param {string} textDecoration - Text decoration value
 * @returns {string} - Tailwind CSS text decoration class
 */
export function getTextDecorationClass(textDecoration) {
  if (!textDecoration || textDecoration === "none") return "";
  return textDecoration.trim();
}

/**
 * Gets Tailwind line height class
 * @param {number|string} lineHeight - Line height value
 * @returns {string} - Tailwind CSS line height class
 */
export function getLineHeightClass(lineHeight) {
  return lineHeight;
}

/**
 * Gets Tailwind letter spacing class
 * @param {number|string} letterSpacing - Letter spacing value
 * @returns {string} - Tailwind CSS letter spacing class
 */
export function getLetterSpacingClass(letterSpacing) {
  return letterSpacing;
}

/**
 * Gets Tailwind text transform class
 * @param {string} textTransform - Text transform value
 * @returns {string} - Tailwind CSS text transform class
 */
export function getTextTransformClass(textTransform) {
  if (!textTransform) return "";
  return textTransform.trim();
}

export function getTextPositionClasses(textPosition, isContainer = true, preview) {
  // ``text_position`` was historically a plain string (e.g. ``"middle-center"``)
  // but is now stored as a ``ResponsiveTextPosition`` wrapper (object with
  // ``extra_small``..``extra_large`` keys). Accept both shapes here so call
  // sites in renderers/editor can pass the raw ``character_style.text_position``
  // value without each one needing to remember to resolve first. ``preview``
  // is forwarded to ``resolveTextPosition`` so editor previews with a fixed
  // viewport pick the right breakpoint instead of using ``window.innerWidth``.
  const resolved =
    typeof textPosition === "string"
      ? textPosition
      : resolveTextPosition(textPosition, preview);

  if (!resolved) {
    return isContainer ? "flex justify-center items-center" : "text-center";
  }

  const [vertical = "middle", horizontal = "center"] = resolved.split("-");

  if (horizontal === "full") {
    return isContainer 
      ? `${VERTICAL_ALIGNMENT_CLASSES[vertical]} text-justify`
      : "text-justify";
  }

  if (!isContainer) {
    // For text elements (like h1), only return text alignment
    const textAlign = {
      left: "text-left",
      center: "text-center", 
      right: "text-right"
    };
    return textAlign[horizontal] || "text-center";
  }

  // For containers, return flex classes
  const vClass = VERTICAL_ALIGNMENT_CLASSES[vertical] || "items-center";
  const hClass =
    HORIZONTAL_ALIGNMENT_CLASSES[horizontal] || "justify-center text-center";

  return `flex ${hClass} ${vClass}`;
}

export function getTextActionTransformClass(
  textTransformEnabled,
  textTransforms,  // Now an array!
  textTransformColor,
  action = "hover",
) {
  if (!textTransformEnabled || !Array.isArray(textTransforms) || textTransforms.length === 0)
    return { className: "", style: {} };

  // Build classes for all transforms except color-shift
  const transformClasses = textTransforms
    .filter(t => t !== 'color-shift')  // Handle color-shift separately
    .map(transform => {
      if (TEXT_TRANSFORM_CLASSES[transform]) {
        return `${action}:${TEXT_TRANSFORM_CLASSES[transform]}`;
      }
      return '';
    })
    .filter(Boolean);
  
  let className = transformClasses.length > 0 
    ? `${TRANSITION_CLASS} ${transformClasses.join(' ')}` 
    : '';
  
  let style = {};
  
  // Handle color-shift if present
  if (textTransforms.includes('color-shift') && textTransformColor) {
    // Add transition class if not already added
    if (!className.includes(TRANSITION_CLASS)) {
      className = `${TRANSITION_CLASS} ${className}`.trim();
    }
    
    if (textTransformColor.startsWith('#')) {
      style['--hover-color'] = textTransformColor;
      className += ` ${action}:[color:var(--hover-color)]`;
    } else {
      const colorClass = TAILWIND_COLOR_REGEX.test(textTransformColor)
        ? `${action}:text-${textTransformColor}`
        : `${action}:text-${textTransformColor}-500`;
      className += ` ${colorClass}`;
    }
  }
  
  // No additional styling needed for underline - handled by Tailwind classes
  
  return { className: className.trim(), style };
}

/**
 * Combines all character style properties into Tailwind classes and custom styles
 * @param {Object} characterStyle - The CharacterStyle object with all text styling properties
 * @returns {Object} - Object with className for Tailwind classes and style for custom CSS
 */
export function getCharacterStyleClasses(characterStyle, preview) {
  if (!characterStyle) {
    return { className: "", style: {} };
  }

  const fontFamilyClass = getFontFamilyClass(characterStyle.font_family);
  const fontFamilyStyle = getFontFamilyStyle(characterStyle.font_family);
  const fontSizePx = resolveFontSizePx(characterStyle, preview);
  const fontSizeClass = getFontSizeClass(fontSizePx);
  const fontWeightClass = getFontWeightClass(
    characterStyle.font_bold,
    characterStyle.font_custom_weight_enabled,
    characterStyle.font_custom_weight,
  );
  const { className: textColorClass, style: textColorStyle } =
    getTextColorStyle(characterStyle.text_color);
  const { className: backgroundColorClass, style: backgroundColorStyle } =
    getBackgroundColorStyle(characterStyle.background_color);
  const fontItalicClass = getFontItalicClass(characterStyle.font_italic);
  const textDecorationClass = getTextDecorationClass(
    characterStyle.text_decoration,
  );
  const lineHeightClass = getLineHeightClass(characterStyle.line_height);
  const letterSpacingClass = getLetterSpacingClass(
    characterStyle.letter_spacing,
  );
  const textTransformClass = getTextTransformClass(
    characterStyle.text_transform,
  );
  const textPositionClasses = getTextPositionClasses(
    characterStyle.text_position,
  );
  const textHoverTransformResult = getTextActionTransformClass(
    characterStyle.text_hover_transform_enabled,
    characterStyle.text_hover_transform,  // Now an array
    characterStyle.text_hover_transform_color,
  );
  const textClickTransformResult = getTextActionTransformClass(
    characterStyle.text_click_transform_enabled,
    characterStyle.text_click_transform,  // Now an array
    characterStyle.text_click_transform_color,
    "active",
  );

  const className = [
    fontFamilyClass,
    fontSizeClass,
    fontWeightClass,
    textColorClass,
    backgroundColorClass,
    fontItalicClass,
    textDecorationClass,
    lineHeightClass,
    letterSpacingClass,
    textTransformClass,
    textPositionClasses,
    textHoverTransformResult.className,  // Extract className
    textClickTransformResult.className,   // Extract className
  ]
    .filter(Boolean)
    .join(" ");

  const style = {
    ...fontFamilyStyle,
    ...textColorStyle,
    ...backgroundColorStyle,
    ...textHoverTransformResult.style,  // Merge styles
    ...textClickTransformResult.style,   // Merge styles
  };

  return { className, style };
}
